// Aktív alap — single source of truth for which Foundation versions
// downstream agents (Vonzás, Értékesítés, and the Foundation cross-references)
// should default to when the user opens any agent.
//
// SERVER = TRUTH. The backend `/api/foundation/active-alap` endpoint stores
// the user's selection per user_id in MongoDB (`user_active_alap` collection).
// The `SidebarAktivAlap` component hydrates from the server on mount + focus,
// mirrors the value into `localStorage.mex_active_alap` so the very next
// render (before the fetch settles) still has the right ids.
//
// This module's helpers read the localStorage MIRROR — good enough as a
// last-mile default resolver inside agent input hydration. If localStorage
// is stale (e.g. user just switched device), the SidebarAktivAlap hydration
// refreshes the state and rerenders the input screen.

const STORAGE_KEY = "mex_active_alap";

export function getActiveAlap() {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return {
      voice: typeof v?.voice === "string" ? v.voice : null,
      client: typeof v?.client === "string" ? v.client : null,
      offer: typeof v?.offer === "string" ? v.offer : null,
    };
  } catch {
    return { voice: null, client: null, offer: null };
  }
}

// Purge every MEX Magic scoped local/session storage key. Called on logout
// so a subsequent login (same device, different account) never leaks the
// previous user's active alap / drafts / preview flags.
export function purgeMexLocalCache() {
  try {
    const drop = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith("mex_") || k.startsWith("mex.")) drop.push(k);
    }
    drop.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
  try {
    const drop = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k) continue;
      if (k.startsWith("mex_") || k.startsWith("mex.")) drop.push(k);
    }
    drop.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* ignore */ }
}

// Resolve the active selection for a given bucket against a loaded array of
// saved blueprints (each item must have `.id`). Returns:
//   - the active item if the active id is present in the bucket,
//   - else the first item (most recent),
//   - else null when the bucket is empty.
export function pickActiveOrLatest(bucketKey, bucket) {
  const active = getActiveAlap()[bucketKey];
  if (active && Array.isArray(bucket)) {
    const found = bucket.find((it) => it && it.id === active);
    if (found) return found;
  }
  return Array.isArray(bucket) && bucket.length > 0 ? bucket[0] : null;
}

// Returns the index of the active item inside the provided bucket, or 0 if
// not found. Useful for components that track selection via index.
export function pickActiveIndex(bucketKey, bucket) {
  const active = getActiveAlap()[bucketKey];
  if (active && Array.isArray(bucket)) {
    const idx = bucket.findIndex((it) => it && it.id === active);
    if (idx >= 0) return idx;
  }
  return 0;
}
